import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiResponse } from '../types/api';

export interface SeasonalDayConditions {
  member?: string;
  t: number;
  rh: number;
  wind: number;
  dir: number | null;
  df: number;
  curing: number;
  ffdi: number;
  category: string;
}

export interface SeasonalLensStats {
  peak: number;
  peak_day: number;
  high_days: number;   // days above this location's historical ~p67 FFDI (worst 33%)
  record: number;      // days above the historical monthly max
  hottest: number;
  over40: number;
  dry: number;
  df: number;
}

export interface SeasonalLens {
  percentile: number;   // 10..90 (deciles) or 99 (extreme tail)
  daily_peak: number[];
  stats: SeasonalLensStats;
  day_conditions: (SeasonalDayConditions | null)[];
  monthly_high: number[];   // per month: days in this lens above the local worst-third
}

export interface SeasonalProvenance {
  model: string;
  source: string;
  members: number;
  resolution: string;
  horizon_days: number;
  timezone: string;
  drought_source: string;
  baseline: string;   // 'climatology' | 'absolute'
  generated_at: string;
}

// Lens keys are percentile-based: 'p10'..'p90' (deciles) and 'p99' (tail).
export type SeasonalLensKey = string;

export interface SeasonalSummary {
  status: 'ready' | 'pending';
  stale?: boolean;
  provenance?: SeasonalProvenance;
  start_date?: string;
  days?: string[];
  bands?: {
    p10: number[];
    p25: number[];
    p50: number[];
    p75: number[];
    p90: number[];
    max: number[];
  };
  local_median?: number[];
  weekly_exceedance?: { week: number; start: string; prob: number }[];
  monthly?: {
    year: number;
    month: number;
    n_days: number;
    worst_peak: number;
    outlook: string;            // 'Below' | 'Near normal' | 'Above' | 'Well above' | '—'
    anomaly: number | null;     // forecast median ÷ local normal median
    forecast_median: number;
    normal_median: number | null;
  }[];
  lenses?: Record<SeasonalLensKey, SeasonalLens>;
  spaghetti?: number[][];
}

export interface SeasonalWindow {
  drought_factor: number;
  interval_weather: Record<
    string,
    { temperature: number; relative_humidity: number; wind_speed: number; wind_direction: number }
  >;
  status?: string;
}

/**
 * Seasonal outlook summary. Lazy on the backend: while the forecast is being
 * (re)computed the response is `{ status: 'pending' }`, so we poll until ready.
 */
export const useSeasonalForecast = (projectId: string) => {
  const query = useQuery({
    queryKey: ['seasonalForecast', projectId],
    queryFn: () =>
      api.request<ApiResponse<SeasonalSummary>>(
        `/api/weather/seasonal-forecast?project_id=${projectId}`
      ),
    enabled: !!projectId,
    refetchInterval: (q) => (q.state.data?.data?.status === 'pending' ? 5000 : false),
  });

  return {
    data: query.data?.data,
    isLoading: query.isLoading,
  };
};

/**
 * Fetch the simulation-ready hourly weather for a chosen ignition window
 * (lens member + start timestamp + duration). Imperative — called at generate
 * time, not as a hook.
 */
export const fetchSeasonalWindow = (
  projectId: string,
  lens: SeasonalLensKey,
  startTs: string,
  hours: number
) =>
  api.request<ApiResponse<SeasonalWindow>>(
    `/api/weather/seasonal-forecast/window?project_id=${projectId}&lens=${lens}` +
      `&start_ts=${encodeURIComponent(startTs)}&hours=${hours}`
  );
